import { inferReadonlyArray } from "@aprao/common/src/array/inferReadonlyArray.js";
import { pull } from "@aprao/common/src/array/pull.js";
import {
  type BigFraction,
  bigFractionFromFraction,
  bigFractionFromNumber,
} from "@aprao/common/src/math/BigFraction.js";
import { divide } from "@aprao/common/src/math/divide.js";
import {
  FRACTION_ONE,
  FRACTION_ZERO,
} from "@aprao/common/src/math/fraction.js";
import { opposite } from "@aprao/common/src/math/opposite.js";
import { sum, sumKey } from "@aprao/common/src/math/sum.js";
import type {
  CountableNumber,
  FundingType,
  PeriodGranularity,
  PositiveInteger,
  WithRelativesSet,
} from "@aprao/common/src/shapes/common.js";
import { combineNullables } from "@aprao/common/src/util/combineNullables.js";
import { times } from "@aprao/common/src/util/times.js";
import { makeAutoObservable, toJS } from "mobx";
import { computedFn } from "mobx-utils";
import { mapForNullable } from "option-t/nullable";

import type {
  OperatedAssetCostKeys,
  OperatedAssetRentKeys,
  OperatingCostKeys,
  ShapeOperatedAssetGroup,
  ShapeOperatedAssetItem,
} from "../../shapes/ShapeRevenueGroup.js";
import {
  type NamedRelativesSetFacade,
  namedRelativesSetFacadeWithAbsolute,
} from "../NamedRelativesSetFacade.js";
import { OperatedAssetItemFacade } from "./OperatedAssetItemFacade.js";
import { RevenueGroupBaseFacade } from "./RevenueGroupBaseFacade.js";
import { SalesGroupBaseFacade } from "./SalesGroupBaseFacade.js";
import { SalesProjectionPeriodsDelegate } from "./SalesProjectionPeriodsDelegate.js";

export type OperatingCostsFacade = NamedRelativesSetFacade<OperatingCostKeys>;
export type OperatedAssetCostFacade =
  NamedRelativesSetFacade<OperatedAssetCostKeys>;

export class OperatedAssetGroupFacade {
  readonly category = "operated";

  readonly $model;

  readonly _revenueGroupBase;

  readonly _salesGroupBase;

  $yieldOffset = FRACTION_ZERO;

  $annualRentalIncomeTotalScale = FRACTION_ONE;

  constructor(model: ShapeOperatedAssetGroup) {
    makeAutoObservable(this);

    this.$model = model;
    this._revenueGroupBase = new RevenueGroupBaseFacade(this.$model);
    this._salesGroupBase = new SalesGroupBaseFacade(this.$model, {
      areaTotal: () => this.areaTotal,
      valueGross: () => this.valueGross,
    });
  }

  get periodGranularity() {
    return this.$model.periodGranularity;
  }

  get rentFreeMonthsCount() {
    return this.$model.rentFreeMonthsCount;
  }

  get yield_() {
    return mapForNullable(this.$model.yield, (value) =>
      bigFractionFromFraction(value).add(this.$yieldOffset),
    );
  }

  readonly _decorateItem = computedFn(
    (item: ShapeOperatedAssetItem) =>
      new OperatedAssetItemFacade(item, {
        periodGranularity: () => this.periodGranularity,
      }),
  );

  get items() {
    return inferReadonlyArray(
      this.$model.items.map((item) => this._decorateItem(item)),
    );
  }

  addItem(params: {
    quantity: CountableNumber;
    description: string;
    area: BigFraction | null;
    periodRent: WithRelativesSet<OperatedAssetRentKeys>;
  }) {
    this.$model.items.push(
      toJS({
        quantity: params.quantity,
        description: params.description,
        area: params.area?.toFraction() ?? null,
        periodRent: params.periodRent,
        weekLetCount: null,
      }),
    );
  }

  removeItem(item: OperatedAssetItemFacade) {
    pull(this.$model.items, item.$model);
  }

  breakdownItem(item: OperatedAssetItemFacade) {
    if (item.quantity < 2) {
      return;
    }

    this.removeItem(item);
    times(item.quantity, () => {
      this.addItem(
        toJS({
          quantity: 1 as CountableNumber,
          description: item.description,
          area: item.area,
          periodRent: { ...item.$model.periodRent },
        }),
      );
    });
  }

  get totalCount() {
    return this.items
      .map(({ quantity }) => quantity)
      .reduce((prev, curr) => prev + curr, 0) as PositiveInteger;
  }

  get areaTotal() {
    return sumKey(this.items, "aggregateArea");
  }

  get annualRentalIncomeTotal() {
    return mapForNullable(
      sumKey(this.items, "aggregateAnnualRentalIncome"),
      (value) => value.mul(this.$annualRentalIncomeTotalScale),
    );
  }

  /**
   * Tweaks the value with the given ratio.
   *
   * This is only used to generate the sensitivity analysis and is not
   * persisted into the model.
   */
  rescaleAnnualRentalIncomeTotal(ratio: BigFraction) {
    this.$annualRentalIncomeTotalScale = ratio;
  }

  readonly _decorateOperatingCostsCost = computedFn(
    (cost: OperatingCostsFacade["$model"]) =>
      namedRelativesSetFacadeWithAbsolute(cost, {
        ratioAnnualRentalIncome: () => this.annualRentalIncomeTotal,
        ratioArea: () => this.areaTotal,
      }),
  );

  get operatingCosts() {
    return inferReadonlyArray(
      this.$model.operatingCosts.map((cost) =>
        this._decorateOperatingCostsCost(cost),
      ),
    );
  }

  get operatingCostsTotal() {
    return sum(
      this.operatingCosts.map((cost) =>
        cost.relativesSet.getRelative("absolute"),
      ),
    );
  }

  get annualIncome() {
    return sum([
      this.annualRentalIncomeTotal,
      opposite(this.operatingCostsTotal),
    ]);
  }

  get valueGross() {
    return mapForNullable(
      combineNullables(
        (annualIncome, yieldPct, rentFreeMonthsCount) => {
          const nbrYield = yieldPct.valueOf();

          return nbrYield === 0 ? 0
              // We have to use native numbers since we cannot exponentiate
              // fraction. The caveat is we are at risk of loosing precision
            : (
              (annualIncome.valueOf() *
                (1 + nbrYield) ** (-rentFreeMonthsCount / 12)) /
                nbrYield
            );
        },
        this.annualIncome,
        this.yield_,
        // use 0 months as default even if the value isn't set
        this.rentFreeMonthsCount ?? 0,
      ),
      (value) => bigFractionFromNumber(value).mul(this.valueGrossScale),
    );
  }

  get projectionPeriods() {
    return new SalesProjectionPeriodsDelegate({
      periods: () => this.$model.projectionPeriods,
      totalCount: () => this.totalCount,
    });
  }

  get projectionDuration() {
    return this.projectionPeriods.duration;
  }

  get meanValue() {
    return this.valueGross?.div(this.totalCount) ?? null;
  }

  getGrossProceedsProjectionAtPeriodIndex = (periodIndex: number) =>
    (this.meanValue ?? FRACTION_ZERO).mul(
      this.projectionPeriods.getCountAtPeriodIndex(periodIndex) ?? 0,
    );

  getSalesCostProjectionAtPeriodIndex = (periodIndex: number) => {
    const costsMean = this.costsMean ?? FRACTION_ZERO;
    return costsMean.mul(
      this.projectionPeriods.getCountAtPeriodIndex(periodIndex) ?? 0,
    );
  };

  addOperatingCost() {
    this.$model.operatingCosts.push({
      name: `Operating cost ${this.$model.operatingCosts.length + 1}`,
      relatives: {
        absolute: null,
        ratioAnnualRentalIncome: null,
        ratioArea: null,
      },
    });
  }

  removeOperatingCost(cost: OperatingCostsFacade) {
    pull(this.$model.operatingCosts, cost.$model);
  }

  set rentFreeMonthsCount(count: number | null) {
    this.$model.rentFreeMonthsCount = count;
  }

  set periodGranularity(granularity: PeriodGranularity) {
    this.$model.periodGranularity = granularity;
  }

  set yield_(yield_: BigFraction | null) {
    this.$model.yield = yield_?.toFraction() ?? null;
  }

  /**
   * Tweaks the yield value by offsetting it.
   *
   * This is only used the sensitivity analysis.
   */
  set yieldOffset(offset: BigFraction) {
    this.$yieldOffset = offset;
  }

  readonly _decorateCost = computedFn(
    (cost: OperatedAssetCostFacade["$model"]) =>
      namedRelativesSetFacadeWithAbsolute(cost, {
        ratioAnnualRentalIncome: () => this.annualRentalIncomeTotal,
        ratioValue: () => this.valueGross,
      }),
  );

  get costs() {
    return inferReadonlyArray(
      this.$model.costs.map((cost) => this._decorateCost(cost)),
    );
  }

  addCost() {
    this.$model.costs.push({
      name: `Custom cost ${this.$model.costs.length + 1}`,
      relatives: {
        absolute: null,
        ratioAnnualRentalIncome: null,
        ratioValue: null,
      },
    });
  }

  removeCost(cost: OperatedAssetCostFacade) {
    pull(this.$model.costs, cost.$model);
  }

  get costsTotal() {
    return sum(
      this.costs.map((cost) => cost.relativesSet.getRelative("absolute")),
    );
  }

  get costsMean() {
    return mapForNullable(this.costsTotal, (cost) =>
      0 < this.totalCount ? cost.div(this.totalCount) : FRACTION_ZERO,
    );
  }

  /**
   * The ratio of costs and fees over the group total value.
   */
  get costsOnValue() {
    return divide(this.costsTotal, this.valueGross);
  }

  // below are all the delegates
  get name() {
    return this._revenueGroupBase.name;
  }

  get fundingType() {
    return this._revenueGroupBase.fundingType;
  }

  get valueGrossScale() {
    return this._revenueGroupBase.valueGrossScale;
  }

  set name(name: string) {
    this._revenueGroupBase.name = name;
  }

  set fundingType(fundingType: FundingType) {
    this._revenueGroupBase.fundingType = fundingType;
  }

  set valueGrossScale(ratio: BigFraction) {
    this._revenueGroupBase.valueGrossScale = ratio;
  }

  get linkedBuildPhaseRefId() {
    return this._salesGroupBase.linkedBuildPhaseRefId;
  }

  get averageValuePerArea() {
    return this._salesGroupBase.averageValuePerArea;
  }
}
